import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { LogIn, Loader2, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import { isTWAContext, isRoleAllowedInTWA } from '../../lib/twaDetection';

export const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, logout } = useAuth();
  const navigate = useNavigate();
  const twaMode = isTWAContext();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const authedUser = await login(email, password);

      // Guard: en modo APK (TWA) sólo se permite el rol ASEGURADO
      if (twaMode && !isRoleAllowedInTWA(authedUser?.role)) {
        await logout();
        toast.error(
          'Esta aplicación móvil es exclusiva para asegurados. Ingresá desde la web para roles administrativos.',
          { duration: 6000 }
        );
        return;
      }

      toast.success('Bienvenido a Horizonte');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al iniciar sesion');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/auth/callback/google';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleAppleLogin = () => {
    toast.info('Apple Login estara disponible proximamente');
  };

  const handleMetaLogin = () => {
    toast.info('Meta Login estara disponible proximamente');
  };

  return (
    <div className="login-page">
      {/* Animated background */}
      <div className="login-bg">
        <div className="login-orb login-orb-1" />
        <div className="login-orb login-orb-2" />
        <div className="login-orb login-orb-3" />
      </div>

      {/* Content */}
      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Logo */}
        <div className="w-full text-center pt-8 sm:pt-14 login-fade-in">
          <img
            src="/logo-blanco.png"
            alt="Seguros Horizonte, S.A."
            className="h-14 sm:h-20 inline-block object-contain login-logo-glow"
          />
        </div>

        {/* Form */}
        <div className="flex-1 flex items-start sm:items-center justify-center px-4 pb-8 pt-6">
          <div className="w-full max-w-sm sm:max-w-md">
            {/* Glass card */}
            <div className="login-card login-fade-in-up">
              {twaMode && (
                <div
                  className="mb-4 flex items-start gap-2 rounded-lg bg-[#279ddc]/10 border border-[#279ddc]/30 px-3 py-2"
                  data-testid="twa-mode-banner"
                >
                  <Smartphone className="w-4 h-4 text-[#052f6b] mt-0.5 shrink-0" />
                  <p className="text-[11px] text-[#052f6b] leading-snug">
                    Estás en la aplicación móvil de Horizonte. Solo asegurados
                    pueden acceder desde aquí.
                  </p>
                </div>
              )}
              <div className="text-center mb-5">
                <h1 className="text-xl sm:text-2xl font-bold text-foreground" data-testid="login-title">
                  Iniciar Sesion
                </h1>
                <p className="text-sm text-muted-foreground mt-1">
                  Accede a tu cuenta para gestionar tus seguros
                </p>
              </div>

              <form onSubmit={handleLogin} className="space-y-3">
                <div className="space-y-1.5 login-stagger-1">
                  <Label htmlFor="email" className="text-xs font-medium">
                    Correo Electronico
                  </Label>
                  <Input
                    id="email"
                    data-testid="login-email-input"
                    type="email"
                    placeholder="tu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    disabled={loading}
                    className="h-11 bg-background/50 border-border/50 focus:border-primary/50 focus:ring-primary/20 transition-all"
                  />
                </div>

                <div className="space-y-1.5 login-stagger-2">
                  <Label htmlFor="password" className="text-xs font-medium">
                    Contrasena
                  </Label>
                  <Input
                    id="password"
                    data-testid="login-password-input"
                    type="password"
                    placeholder="********"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    disabled={loading}
                    className="h-11 bg-background/50 border-border/50 focus:border-primary/50 focus:ring-primary/20 transition-all"
                  />
                </div>

                <Button
                  type="submit"
                  data-testid="login-submit-button"
                  className="w-full h-11 font-semibold text-sm login-stagger-3 login-btn-primary"
                  disabled={loading}
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Ingresando...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <LogIn className="w-4 h-4" />
                      Iniciar Sesion
                    </span>
                  )}
                </Button>
              </form>

              <div className="relative my-4 login-stagger-4">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-border/40" />
                </div>
                <div className="relative flex justify-center text-[10px] uppercase">
                  <span className="bg-white px-3 text-muted-foreground font-medium">
                    O continua con
                  </span>
                </div>
              </div>

              <div className="space-y-2 login-stagger-5">
                <Button
                  type="button"
                  variant="outline"
                  data-testid="google-login-button"
                  className="w-full h-10 hover:bg-muted/80 transition-all"
                  onClick={handleGoogleLogin}
                  disabled={loading}
                >
                  <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continuar con Google
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  data-testid="apple-login-button"
                  className="w-full h-10 hover:bg-muted/80 transition-all"
                  onClick={handleAppleLogin}
                  disabled={loading}
                >
                  <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                  </svg>
                  Continuar con Apple
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  data-testid="meta-login-button"
                  className="w-full h-10 hover:bg-muted/80 transition-all"
                  onClick={handleMetaLogin}
                  disabled={loading}
                >
                  <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="#1877F2">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                  Continuar con Facebook
                </Button>
              </div>

              <div className="text-center text-xs pt-3 login-stagger-5">
                <a href="#" className="text-primary hover:underline font-medium">
                  Olvidaste tu contrasena?
                </a>
              </div>
            </div>

            <p className="text-center text-xs text-white/70 mt-5 login-fade-in-up" style={{ animationDelay: '0.6s' }}>
              No tienes cuenta?{' '}
              <Link to="/register" className="text-white font-semibold hover:underline">
                Registrarse
              </Link>
            </p>

            <p className="text-center text-xs tracking-[0.08em] text-white/85 mt-6 font-semibold italic login-fade-in" style={{ animationDelay: '0.7s', fontFamily: "'Libre Baskerville', Georgia, serif" }} data-testid="login-brand-slogan">
              Tu Bienestar es Nuestro Horizonte
            </p>
          </div>
        </div>
      </div>

      <style>{`
        .login-page {
          position: relative;
          min-height: 100vh;
          overflow: hidden;
          background: #001a3a;
        }

        .login-bg {
          position: absolute;
          inset: 0;
          background: radial-gradient(ellipse at 30% 20%, #052f6b 0%, transparent 50%),
                      radial-gradient(ellipse at 70% 80%, #0a1628 0%, transparent 50%),
                      linear-gradient(180deg, #001a3a 0%, #0d2847 50%, #001a3a 100%);
        }

        .login-orb {
          position: absolute;
          border-radius: 50%;
          filter: blur(80px);
          opacity: 0.4;
          will-change: transform;
        }

        .login-orb-1 {
          width: 400px;
          height: 400px;
          background: #279ddc;
          top: -10%;
          right: -5%;
          animation: orb-float 20s ease-in-out infinite;
        }

        .login-orb-2 {
          width: 300px;
          height: 300px;
          background: #09d7d7;
          bottom: 10%;
          left: -5%;
          animation: orb-float 25s ease-in-out infinite reverse;
          opacity: 0.25;
        }

        .login-orb-3 {
          width: 200px;
          height: 200px;
          background: #052f6b;
          top: 50%;
          left: 50%;
          animation: orb-float 18s ease-in-out infinite;
          opacity: 0.3;
        }

        @keyframes orb-float {
          0%, 100% { transform: translate(0, 0) scale(1); }
          25% { transform: translate(30px, -20px) scale(1.05); }
          50% { transform: translate(-20px, 30px) scale(0.95); }
          75% { transform: translate(20px, 20px) scale(1.02); }
        }

        .login-logo-glow {
          filter: drop-shadow(0 0 20px rgba(39, 157, 220, 0.3));
          animation: logo-pulse 4s ease-in-out infinite;
        }

        @keyframes logo-pulse {
          0%, 100% { filter: drop-shadow(0 0 20px rgba(39, 157, 220, 0.2)); }
          50% { filter: drop-shadow(0 0 30px rgba(39, 157, 220, 0.4)); }
        }

        .login-card {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 20px;
          padding: 28px 24px;
          box-shadow: 
            0 25px 50px rgba(0, 0, 0, 0.3),
            0 0 0 1px rgba(255, 255, 255, 0.05),
            inset 0 1px 0 rgba(255, 255, 255, 0.5);
        }

        @media (min-width: 640px) {
          .login-card { padding: 36px 32px; }
        }

        .login-btn-primary {
          background: linear-gradient(135deg, #052f6b, #279ddc);
          border: none;
          box-shadow: 0 4px 15px rgba(39, 157, 220, 0.3);
          transition: transform 0.2s, box-shadow 0.2s;
        }
        .login-btn-primary:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 6px 20px rgba(39, 157, 220, 0.4);
        }
        .login-btn-primary:active:not(:disabled) {
          transform: translateY(0);
        }

        .login-fade-in {
          animation: fadeIn 0.8s ease-out both;
        }

        .login-fade-in-up {
          animation: fadeInUp 0.6s ease-out both;
          animation-delay: 0.2s;
        }

        .login-stagger-1 { animation: fadeInUp 0.5s ease-out both; animation-delay: 0.15s; }
        .login-stagger-2 { animation: fadeInUp 0.5s ease-out both; animation-delay: 0.25s; }
        .login-stagger-3 { animation: fadeInUp 0.5s ease-out both; animation-delay: 0.35s; }
        .login-stagger-4 { animation: fadeInUp 0.5s ease-out both; animation-delay: 0.4s; }
        .login-stagger-5 { animation: fadeInUp 0.5s ease-out both; animation-delay: 0.45s; }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};
